/**
 * SchedulingDispatchCockpit — 2-lens unified Scheduling & Dispatch surface.
 *
 * Apple-flavored restraint: one decision per lens, intelligence surfaced
 * inline (not as a separate tab). Mirrors the rhythm of /marketing-performance.
 *
 *   • Plan      — calendar. Book installs, see the week. Conflicts and crew
 *                 utilization render *inside* the calendar (already built into
 *                 SchedulingCalendarView via useSchedulingConflicts +
 *                 useCrewUtilization). No separate analytics tab.
 *   • Dispatch  — today's prioritized job queue. Who needs to be sent,
 *                 blockers, payment-due, KPI strip.
 *
 * Above the lens tabs sits a shared KPI strip — the "how's today?" headline
 * answer, visible regardless of which lens is active.
 *
 * Routed from /scheduling, /scheduling-dispatch, and /dispatch-intelligence
 * in Cockpit. Classic mode is preserved untouched (route switchers handle that).
 *
 * Retired vs prior 3-lens version:
 *   • Standalone "Intelligence" lens → its calendar overlapped Plan; its
 *     conflict/utilization signals are already inline in Plan; contractor
 *     scorecards belong in Reporting, not here.
 */
import { useState, useEffect, lazy, Suspense, useMemo } from "react";
import { useSearchParams, useNavigate, useLocation, Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { ArrowRight, CalendarDays, ClipboardCheck, Map as MapIcon, Send } from "lucide-react";
import { CockpitPage } from "@/components/cockpit/CockpitPage";
import { OutcomeKpiStrip } from "@/components/scheduling/OutcomeKpiStrip";
import { PlanCockpit } from "@/components/scheduling/PlanCockpit";
import { PreInstallConfirmationQueue } from "@/components/scheduling/PreInstallConfirmationQueue";
import { useAppData } from "@/data/AppDataContext";
import { SCHEDULING_DISPATCH_SERVER_PAGING_ENABLED } from "@/lib/dataRuntimeFlags";
import { isSchedulingDispatchServerPagedRoute } from "@/lib/routeScope";

const DispatchLens = lazy(() => import("@/pages/DispatchIntelligenceCockpit")); // today's queue

type Lens = "plan" | "dispatch";

const LENS_FROM_PATH: Record<string, Lens> = {
  "/scheduling": "plan",
  "/scheduling-dispatch": "dispatch",
  "/dispatch-intelligence": "dispatch",
};

const VALID_LENSES: Lens[] = ["plan", "dispatch"];

export default function SchedulingDispatchCockpit() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Resolve initial lens. Accept legacy ?lens=schedule|intelligence so old
  // deep-links don't break — both fold into "plan".
  const rawParam = searchParams.get("lens");
  const paramLens: Lens | null =
    rawParam === "plan" || rawParam === "dispatch"
      ? rawParam
      : rawParam === "schedule" || rawParam === "intelligence"
      ? "plan"
      : null;

  const initialLens: Lens =
    paramLens ?? LENS_FROM_PATH[location.pathname] ?? "dispatch";

  const [lens, setLens] = useState<Lens>(initialLens);
  const serverPagedDispatchLens =
    lens === "dispatch" &&
    SCHEDULING_DISPATCH_SERVER_PAGING_ENABLED &&
    isSchedulingDispatchServerPagedRoute(location.pathname, location.search);

  // Keep ?lens= in URL sync without remounting.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (next.get("lens") !== lens) {
      next.set("lens", lens);
      setSearchParams(next, { replace: true });
    }
  }, [lens, searchParams, setSearchParams]);

  // Normalize legacy URLs to /scheduling-dispatch.
  useEffect(() => {
    if (
      location.pathname === "/scheduling" ||
      location.pathname === "/dispatch-intelligence"
    ) {
      navigate(`/scheduling-dispatch?lens=${lens}`, { replace: true });
    }
  }, [location.pathname, lens, navigate]);

  return (
    <div className="max-w-[1400px] mx-auto pb-12 pt-2">

      {/* Header — shared across both lenses */}
      <CockpitPage
      title="Scheduling"
      maxWidth="7xl"
      controls={
        <LensPills value={lens} onChange={setLens} />
      }
      belowHeader={serverPagedDispatchLens ? null : <OutcomeKpiStrip />}
    >

      <Suspense fallback={<LensSkeleton />}>
        {lens === "plan" && (
          <>
            <PlanCockpit />
            <div className="mt-4">
              <PreInstallConfirmationQueue />
            </div>
          </>
        )}
        {lens === "dispatch" && (
          <>
            <DispatchHandoffBanner />
            <DispatchLens embedded />
          </>
        )}
      </Suspense>
      </CockpitPage>
    </div>
  );
}

// ── Shared KPI strip ──────────────────────────────────────────────────────

// (SharedKpiStrip + KpiTile retired — replaced by OutcomeKpiStrip.)



function LensTab({ active, onClick, icon, label, sub }: {
  active: boolean; onClick: () => void; icon: React.ReactNode; label: string; sub: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "flex items-center gap-2 px-3.5 py-2 rounded-md text-left transition-all duration-200",
        active
          ? "bg-primary text-primary-foreground shadow-md ring-1 ring-primary/40"
          : "text-muted-foreground hover:text-foreground hover:bg-background/60",
      )}
    >
      {icon}
      <span className="flex flex-col leading-tight">
        <span className="text-[12px] font-semibold">{label}</span>
        <span className={cn(
          "text-[10px]",
          active ? "text-primary-foreground/90" : "text-muted-foreground/70",
        )}>
          {sub}
        </span>
      </span>
    </button>
  );
}

function LensPills({ value, onChange }: { value: Lens; onChange: (v: Lens) => void }) {
  const options: { id: Lens; label: string; icon: React.ReactNode }[] = [
    { id: "plan", label: "Plan", icon: <CalendarDays className="h-3.5 w-3.5" /> },
    { id: "dispatch", label: "Dispatch", icon: <Send className="h-3.5 w-3.5" /> },
  ];
  return (
    <div
      role="tablist"
      aria-label="Scheduling lens"
      className="inline-flex items-center rounded-lg border border-border/60 bg-muted/30 p-0.5"
    >
      {options.map(opt => {
        const active = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.id)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-semibold transition-all duration-200",
              active
                ? "bg-primary text-primary-foreground shadow-md ring-1 ring-primary/40"
                : "text-muted-foreground hover:text-foreground hover:bg-background/60",
            )}
          >
            {opt.icon}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function LensSkeleton() {
  return (
    <div className="space-y-3 animate-pulse">
      <div className="h-8 w-48 rounded-md bg-muted/40" />
      <div className="h-64 w-full rounded-md bg-muted/30" />
    </div>
  );
}


function DispatchHandoffBanner() {
  const { jobs } = useAppData();
  const recentlyDispatched = useMemo(() => {
    const cutoff = Date.now() - 30 * 60 * 1000;
    return jobs.filter((j) => {
      if (j.currentStage !== "Assigned and Dispatched" && j.currentStage !== "In Progress") return false;
      const last = j.activityHistory?.[j.activityHistory.length - 1];
      if (!last) return false;
      const ts = new Date(last.date).getTime();
      return ts >= cutoff && /dispatch|assigned/i.test(last.action);
    });
  }, [jobs]);

  if (recentlyDispatched.length === 0) return null;
  const count = recentlyDispatched.length;
  return (
    <div className="mb-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 min-w-0">
        <ClipboardCheck className="h-4 w-4 text-primary shrink-0" />
        <p className="text-xs font-medium text-foreground truncate">
          {count === 1 ? "Job now in Install lane" : `${count} jobs now in Install lane`}
          <span className="text-muted-foreground font-normal"> · confirm readiness handoff</span>
        </p>
      </div>
      <Link
        to="/install-readiness"
        className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline shrink-0"
      >
        Open Install Readiness <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
  );
}
